import React from "react";
import { cn } from "@/lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outlinePrimary" | "outlineSecondary" | "dark" | "outlineDark" | "ghost" | "accent" | "outlineAccent" | "linkPrimary";
  size?: "sm" | "md" | "lg";
  children: React.ReactNode;
  asChild?: boolean;
  arrow?: "none" | "hover" | "grow";
  arrowColorClass?: string;
  disclaimer?: string;
  // add disclaimer text color option
  disclaimerTextColor?: string;
}

const NewButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", asChild = false, arrow = "none", arrowColorClass, disclaimer, disclaimerTextColor = "text-text-secondary", children, ...props }, ref) => {
    const variantClasses = {
      primary: "btn-primary",
      accent: "btn-accent",
      secondary: "btn-secondary",
      outlinePrimary: "btn-outline-primary",
      outlineSecondary: "btn-outline-secondary",
      outlineAccent: "btn-outline-accent",
      dark: "btn-dark",
      outlineDark: "btn-outline-dark",
      ghost: "btn-ghost",
      linkPrimary: "link-primary",
    };

    const sizeClasses = {
      sm: "btn-sm",
      md: "btn-md",
      lg: "btn-lg",
    };

    const effectiveArrow = variant === "linkPrimary" && arrow === "none" ? "grow" : arrow;
    const effectiveArrowColorClass =
      variant === "linkPrimary" && !arrowColorClass ? "stroke-accent" : arrowColorClass;

    const renderArrow = () => {
      if (effectiveArrow === "none") return null;

      const arrowClasses = cn(
        "items-center justify-center transform-gpu transition-all duration-500 ease-out",
        {
          "block scale-0 group-hover:scale-100 translate-x-2 group-hover:translate-x-0 group-hover:delay-0 transition-all duration-300 ease-in-out":
            effectiveArrow === "hover",
          "block scale-100 group-hover:scale-110 group-hover:delay-0": effectiveArrow === "grow",
        }
      );

      return (
        <span className={arrowClasses}>
          {effectiveArrow === "hover" ? (
            <svg
              viewBox="0 0 24 24"
              className={cn(
                "size-5 stroke-[2px] fill-none transform-gpu transition-all duration-500 ease-out",
                effectiveArrowColorClass ?? "stroke-white",
              )}
            >
              <path d="M5 12h14m-7-7l7 7-7 7" className="transform-gpu transition-transform duration-500 ease-out"/>
            </svg>
          ) : (
            <svg
              viewBox="0 0 20 20"
              className={cn(
                "size-6 stroke-[1.5px] fill-none transform-gpu transition-all duration-500 ease-out",
                effectiveArrowColorClass ?? "stroke-white",
              )}
            >
              <line x1="0" y1="10" x2="29" y2="10" className="origin-left -translate-x-[12px] scale-x-75 group-hover:scale-x-100 transform-gpu transition-transform duration-500 ease-out"/>
              <polyline points="9 5 14 10 9 15" className="-translate-x-[3px] group-hover:translate-x-[3px] transform-gpu transition-transform duration-500 ease-out"/>
            </svg>
          )}
        </span>
      );
    };

    const renderButtonContent = () => {
      if (effectiveArrow === "none") {
        return children;
      }

      return (
        <span className="flex items-center justify-center w-full">
          <span className={cn(
            "transform-gpu transition-transform duration-300 ease-in-out",
            {
              "group-hover:delay-100": effectiveArrow === "hover",
              "group-hover:delay-200": effectiveArrow === "grow",
            }
          )}>
            {children}
          </span>
          <span className={cn(
            "transition-all duration-300 ease-in-out overflow-hidden",
            {
              "w-0 group-hover:w-6 ml-0 group-hover:ml-1.5": effectiveArrow === "hover",
              "w-6 ml-1.5": effectiveArrow === "grow",
            }
          )}>
            {renderArrow()}
          </span>
        </span>
      );
    };

    const buttonContent = renderButtonContent();

    const buttonElement = asChild ? (
      (() => {
        return (
          <div
            className={cn(
              variantClasses[variant],
              sizeClasses[size],
              "group",
              variant === "linkPrimary" ? "h-auto w-auto" : "h-[56px] w-[248px]",
              className
            )}
            {...(props as React.HTMLAttributes<HTMLDivElement>)}
          >
            {buttonContent}
          </div>
        );
      })()
    ) : (
      <button
        type="button"
        className={cn(variantClasses[variant], sizeClasses[size], "group", className)}
        ref={ref}
        {...props}
      >
        {buttonContent}
      </button>
    );

    if (disclaimer) {
      return (
        <div className="flex flex-col items-center">
          {buttonElement}
          <span className={cn("text-xs italic mt-1 text-center", disclaimerTextColor)}>
            {disclaimer}
          </span>
        </div>
      );
    }

    return buttonElement;
  }
);

NewButton.displayName = "NewButton";

export { NewButton };
